import { useCallback, useEffect, useRef, useState } from 'react';
import { formatRelative } from '../dashboard/dates';
import { api, type AuditEntry } from '../lib/api';
import { Icon } from '../ui/icons';
import { AUDIT_FILTERS, auditAction, auditIcon, auditTone } from './auditText';

const PAGE = 60;

/**
 * The journal.
 *
 * One component serves two audiences: the super admin reading every
 * organisation (`orgId` omitted) and an org owner reading their own
 * (`orgId` set). The difference is which endpoint answers and whether the
 * organisation is worth naming in each row, so it is not worth two screens.
 */
export function AuditTab({ orgId }: { orgId?: string }) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Changing a filter mid-request must not let the older response land last
  // and show rows for the wrong filter.
  const requestRef = useRef(0);

  const load = useCallback(
    async (before?: number) => {
      const id = ++requestRef.current;
      setLoading(true);
      setError(null);
      try {
        const page = await api.listAudit({
          ...(orgId ? { orgId } : {}),
          ...(action ? { action } : {}),
          ...(before !== undefined ? { before } : {}),
          limit: PAGE,
        });
        if (id !== requestRef.current) return;
        setEntries((current) => (before === undefined ? page : [...current, ...page]));
        setDone(page.length < PAGE);
      } catch {
        if (id === requestRef.current) setError('Не удалось загрузить журнал');
      } finally {
        if (id === requestRef.current) setLoading(false);
      }
    },
    [action, orgId],
  );

  useEffect(() => {
    setEntries([]);
    setDone(false);
    void load();
  }, [load]);

  const oldest = entries[entries.length - 1];

  return (
    <div className="admin-tab audit-tab">
      <div className="audit-toolbar">
        <select
          className="org-member-role"
          value={action}
          onChange={(e) => setAction(e.target.value)}
        >
          {AUDIT_FILTERS.map((filter) => (
            <option key={filter.value} value={filter.value}>
              {filter.label}
            </option>
          ))}
        </select>
        <button type="button" className="ghost-btn" onClick={() => void load()}>
          <Icon name="refresh" size={14} /> Обновить
        </button>
      </div>

      {error && <div className="dash-error">{error}</div>}

      {!loading && entries.length === 0 && (
        <div className="dash-empty">
          <Icon name="shield" size={26} />
          <h2>Журнал пуст</h2>
          <p>Здесь появятся действия участников: создание, удаление, рисование на досках.</p>
        </div>
      )}

      <ul className="audit-list">
        {entries.map((entry) => (
          <li key={entry.id} className={`audit-row is-${auditTone(entry.action)}`}>
            <span className="audit-icon">
              <Icon name={auditIcon(entry.action)} size={15} />
            </span>
            <span className="audit-body">
              <span className="audit-text">
                <strong>{entry.actorName}</strong> {auditAction(entry)}
              </span>
              <span className="audit-meta">
                {formatRelative(new Date(entry.at).toISOString())}
                {entry.actorEmail && ` · ${entry.actorEmail}`}
                {!orgId && entry.orgName && ` · ${entry.orgName}`}
              </span>
            </span>
          </li>
        ))}
      </ul>

      {loading && <div className="dash-loading">Загрузка…</div>}

      {!loading && !done && oldest && (
        <button type="button" className="ghost-btn audit-more" onClick={() => void load(oldest.at)}>
          Показать ещё
        </button>
      )}
    </div>
  );
}
